import {
  ColDef,
  TooltipCallbackParams,
  ValueFormatterParams,
  ValueGetterParams
} from 'ag-grid-community';
import { flatten } from 'lodash';

import { homebaseBaseUrl } from '@/utils/homebaseLink';

import { BillingRowStatus } from './components/BillingRowStatus';
import ClaimIdGridCell from './components/grid_cells/ClaimIdGridCell';
import { FullNameGridCell } from './components/grid_cells/FullNameGridCell';
import IndicatorsGridCell from './components/grid_cells/IndicatorsGridCell';
import ServiceTypeGridCell from './components/grid_cells/ServiceTypeGridCell';
import { BillingReportRow, ErrorsValue } from './types';
import {
  colWithErrorsColDef,
  dateColWithErrorsColDef,
  dateTimeColWithErrorsColDef,
  sensitiveColWithErrorsColDef
} from './utils/agGridColDefs';
import { agGridCurrencyFormatter } from './utils/currencyFormatter';
import {
  INDICATOR_FILTER_OPTIONS,
  rowFlagFilterParams,
  STATUS_FILTER_OPTIONS
} from './utils/rowFlagFilterOptions';
import {
  DATE_ROW_FIELDS,
  NUMERIC_ROW_FIELDS,
  REAL_ALIAS_ROW_FIELDS,
  SERVER_FILTERABLE_ONLY_ROW_FIELDS,
  SERVER_SORTABLE_ROW_FIELDS
} from './utils/serverRowFields';
import { titleize } from './utils/titleize';

export interface CreateBillingRowColumnDefsOptions {
  /**
   * Set for grids backed by a paginated server-side datasource (e.g.
   * BillingItemsList), as opposed to BillingReportResultApp's client-side
   * row model which holds every row in memory and can sort/filter on any
   * field for free. When true, sort/filter UI is disabled for any column
   * the backend can't actually sort/filter on (see serverRowFields.ts) --
   * rather than silently ignoring requests for unsupported fields, we
   * remove the ability to ask for them.
   */
  serverSide?: boolean;
}

// The server-backed grid (BillingItemsList) holds only the current page,
// already filtered server-side via translateFilterModel -- but its default
// (client-side) row model still re-applies the column's own filter to that
// page in memory, so the two need to agree on boundary inclusivity or a row
// sitting exactly on a boundary would pass the server's query but then get
// silently dropped from the page by the grid's own re-filter, while
// total_pages/count still count it.
//
// Numeric columns drop the strict greaterThan/lessThan operators: the server
// only exposes an inclusive _min/_max (shared with greaterThanOrEqual), so
// there's no server param whose bounds agree with AG Grid's strict compare.
// Date columns keep every operator -- translateFilterModel routes strict
// greaterThan/lessThan to the exclusive _after/_before params, and
// inRangeInclusive below makes AG Grid's own inRange agree with the server's
// inclusive _from/_to.
const NUMBER_FILTER_OPTIONS = [
  'equals',
  'notEqual',
  'greaterThanOrEqual',
  'lessThanOrEqual',
  'blank',
  'notBlank'
];

const applyServerSideCapabilities = (colDef: ColDef): ColDef => {
  const field = colDef.field;

  if (field && SERVER_FILTERABLE_ONLY_ROW_FIELDS.has(field)) {
    return { ...colDef, sortable: false };
  }

  if (!field || !SERVER_SORTABLE_ROW_FIELDS.has(field)) {
    return { ...colDef, sortable: false, filter: false };
  }

  if (NUMERIC_ROW_FIELDS.has(field)) {
    return {
      ...colDef,
      filter: 'agNumberColumnFilter',
      filterParams: { ...colDef.filterParams, filterOptions: NUMBER_FILTER_OPTIONS }
    };
  }

  if (DATE_ROW_FIELDS.has(field) || REAL_ALIAS_ROW_FIELDS.has(field)) {
    return {
      ...colDef,
      filterParams: { ...colDef.filterParams, inRangeInclusive: true }
    };
  }

  return colDef;
};

/**
 * The billing item row column definitions, shared by the billing report grid
 * (BillingReportResultApp) and any other grid rendering the same cached row
 * data (e.g. BillingItemsList), so both render rows identically.
 */
export const createBillingRowColumnDefs = (
  host: string,
  options: CreateBillingRowColumnDefsOptions = {}
): ColDef[] => {
  const colDefs: ColDef[] = [
    {
      cellRenderer: BillingRowStatus,
      field: 'errors',
      filter: 'agTextColumnFilter',
      // Has/no errors and warnings, rather than text operators on the raw
      // errors dict (see rowFlagFilterOptions).
      filterParams: rowFlagFilterParams(STATUS_FILTER_OPTIONS),
      filterValueGetter: (params: ValueGetterParams<BillingReportRow>) => params.data,
      headerName: 'Status',

      resizable: true,
      sortable: true,
      suppressAutoSize: true,
      width: 50,
      valueFormatter: (params: ValueFormatterParams<BillingReportRow, ErrorsValue>) => {
        if (params.value) {
          return flatten(Object.values(params.value)).join(', ');
        }
        return '';
      }
    },
    colWithErrorsColDef('warnings', {
      hide: true,
      valueFormatter: (params: ValueFormatterParams<BillingReportRow, ErrorsValue>) => {
        if (params?.value) {
          return flatten(Object.values(params.value)).join(', ');
        }
        return '';
      }
    }),
    colWithErrorsColDef('indicators', {
      headerName: 'i',
      width: 50,
      suppressAutoSize: true,
      // One option per indicator rather than text operators on the raw
      // indicators list (see rowFlagFilterOptions).
      filterParams: rowFlagFilterParams(INDICATOR_FILTER_OPTIONS),
      filterValueGetter: (params: ValueGetterParams<BillingReportRow>) => params.data,
      cellRenderer: IndicatorsGridCell,
      tooltip: (params: TooltipCallbackParams<BillingReportRow, string[]>) => {
        if (!params.value) return null;

        return titleize(params.value?.join(', '));
      }
    }),
    colWithErrorsColDef('on_hold', {
      headerName: 'Held',
      hide: true,
      // on_hold is boolean. Declare it explicitly so both grids agree on the
      // filter shape: the client-side report grid would otherwise infer
      // 'boolean' from row data while the server-backed infinite grid (no rows
      // to infer from) falls back to a text filter -- and a filter model that
      // fits one is silently dropped by the other. With 'boolean' set, both use
      // AG Grid's boolean filter (type: 'true'/'false').
      cellDataType: 'boolean'
    }),
    colWithErrorsColDef('is_case_rate_encounter', {
      headerName: 'Case Rate Encounter',
      hide: true
    }),
    colWithErrorsColDef('cms_1500_healthie_id', {
      hide: true
    }),
    colWithErrorsColDef('person_id', {
      hide: true
    }),
    colWithErrorsColDef('billing_item_id', {
      hide: true
    }),
    colWithErrorsColDef('full_name', {
      cellRenderer: FullNameGridCell,
      width: 200,
      suppressAutoSize: true
    }),
    {
      field: 'claim_id',
      headerName: 'Claim ID #',
      filter: 'agTextColumnFilter',
      comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
        const a = (nodeA.data?.cms_1500_healthie_id || '') + (nodeA.data?.claim_group_id || '');
        const b = (nodeB.data?.cms_1500_healthie_id || '') + (nodeB.data?.claim_group_id || '');
        if (a && b) {
          return isDescending ? b.localeCompare(a) : a.localeCompare(b);
        } else if (a) {
          return isDescending ? -1 : 1;
        } else if (b) {
          return isDescending ? 1 : -1;
        } else {
          return 0;
        }
      },

      cellRenderer: ClaimIdGridCell,
      valueGetter: (params: ValueGetterParams<BillingReportRow>) => {
        const cms1500HealthieId = params.data?.cms_1500_healthie_id || '';
        const claimGroupId = params.data?.claim_group_id || '';

        return cms1500HealthieId + claimGroupId;
      }
    },
    colWithErrorsColDef('service_id', {
      headerName: 'SID',
      suppressAutoSize: true,
      width: 70
    }),
    dateColWithErrorsColDef('service_date'),
    colWithErrorsColDef('service_type', {
      cellRenderer: ServiceTypeGridCell
    }),
    colWithErrorsColDef('destination'),
    colWithErrorsColDef('cpt_code', { headerName: 'CPT' }),
    colWithErrorsColDef('rate', {
      filter: 'agNumberColumnFilter',
      valueFormatter: agGridCurrencyFormatter
    }),
    colWithErrorsColDef('units'),
    colWithErrorsColDef('total_charged', {
      headerName: 'Charged',
      filter: 'agNumberColumnFilter',
      valueFormatter: agGridCurrencyFormatter
    }),
    colWithErrorsColDef('total_expected_revenue', {
      headerName: 'Expected Revenue',
      filter: 'agNumberColumnFilter',
      valueFormatter: agGridCurrencyFormatter
    }),
    colWithErrorsColDef('modifiers'),
    dateColWithErrorsColDef('period_start_at'),
    dateColWithErrorsColDef('period_end_at'),
    colWithErrorsColDef('encounters_count', {}),
    colWithErrorsColDef('jurisdiction'),
    dateColWithErrorsColDef('admitted_at'),
    sensitiveColWithErrorsColDef('sex'),
    colWithErrorsColDef('program_name'),
    colWithErrorsColDef('program_scope'),
    colWithErrorsColDef('billing_provider_name_id'),
    colWithErrorsColDef('service_location'),
    colWithErrorsColDef('phase_name'),
    colWithErrorsColDef('rendering_provider'),
    colWithErrorsColDef('rendering_provider_other_id_qualifier'),
    colWithErrorsColDef('rendering_provider_other_id_number'),
    colWithErrorsColDef('servicing_provider'),
    colWithErrorsColDef('servicing_provider_credentials'),
    colWithErrorsColDef('servicing_provider_role'),
    colWithErrorsColDef('primary_insurance_plan'),
    sensitiveColWithErrorsColDef('insurance_policy'),
    colWithErrorsColDef('plan'),
    colWithErrorsColDef('priority_type'),
    dateColWithErrorsColDef('discharged_at'),
    colWithErrorsColDef('insurance_type'),
    colWithErrorsColDef('actual_duration'),
    colWithErrorsColDef('actual_duration_source'),
    colWithErrorsColDef('scheduled_duration'),
    sensitiveColWithErrorsColDef('attendees'),
    colWithErrorsColDef('session_status'),
    colWithErrorsColDef('conducted_via'),
    colWithErrorsColDef('complexity_level'),
    colWithErrorsColDef('drug_screen_kind'),
    colWithErrorsColDef('drug_screen_result'),
    colWithErrorsColDef('chart_id', {
      cellRenderer: function (params: { value: string }) {
        return (
          <a
            href={`${homebaseBaseUrl(host)}/agent/forms?healthie_id=${params.value}`}
            target="_blank"
            rel="noopener noreferrer">
            {params.value}
          </a>
        );
      }
    }),
    colWithErrorsColDef('chart_filled_by'),
    colWithErrorsColDef('chart_locked_by'),
    colWithErrorsColDef('chart_locking_role'),
    colWithErrorsColDef('rendering_provider_mode'),
    sensitiveColWithErrorsColDef('diagnosis_code1'),
    sensitiveColWithErrorsColDef('diagnosis_code2'),
    sensitiveColWithErrorsColDef('diagnosis_code3'),
    sensitiveColWithErrorsColDef('diagnosis_code4'),
    colWithErrorsColDef('service_class'),
    colWithErrorsColDef('appointment_id'),
    colWithErrorsColDef('healthie_errors'),
    dateTimeColWithErrorsColDef('ignore_error_at'),
    colWithErrorsColDef('pending_cms_1500_healthie_id', {
      hide: true
    }),
    colWithErrorsColDef('manually_associated_by'),
    dateTimeColWithErrorsColDef('manually_associated_at'),
    dateTimeColWithErrorsColDef('manually_held_at')
  ];

  return options.serverSide ? colDefs.map(applyServerSideCapabilities) : colDefs;
};
